import { Fragment, useEffect, useState } from 'react';

import { Button, Divider, Paragraph1, Paragraph2, SmallText } from 'inpock-ui-react';
import { isAxiosError } from 'axios';
import { useQueryClient } from '@tanstack/react-query';

import { PAY_TYPE, REFUND_ERROR_MESSAGE, REFUND_ERROR_TEMPLATE } from '@/constants';
import { api, OrderCalculateRefundResponse, DetailedOrderLine } from '@/apis';
import {
  DashDivider,
  ExternalModalProps,
  Modal,
  MODAL_TYPES,
  SelectBox,
  Thumbnail,
} from '@/components';
import { formatPayType, thumbnail } from '@/utils';
import { ORDER_TEMPLATE, openPopup } from '@/utils';
import { useDialog } from '@/hooks';
import { keys } from '@/hooks/queries/keys';

import * as S from './styled';

const REFUND_MODAL_STEP_MESSAGE = {
  1: '취소할 상품을 선택해주세요',
  2: '취소할 상품을 다시 한 번 확인해 주세요',
};

interface RefundModalProps {
  order_number: string;
  orderlines: DetailedOrderLine[];
  paymentInfo: {
    pay_type: keyof typeof PAY_TYPE;
    card_issuer: string | null;
    installment_plan_months: number | null;
  };
  onClickCancel: (
    cancel_data: { orderline_number: string; quantity: number }[],
    cancel_amount: number,
  ) => void;
  loading: boolean;
  csChannel: string;
}

interface RefundModalStep1Props {
  orderlines: (DetailedOrderLine & { cancelQuantity: number })[];
  checkedOrderlines: (DetailedOrderLine & { cancelQuantity: number })[];
  setCheckedOrderlines: React.Dispatch<
    React.SetStateAction<(DetailedOrderLine & { cancelQuantity: number })[]>
  >;
  onCloseModal?: () => void;
  handleQuantityChange: (orderlineId: number, cancelQuantity: number) => void;
  onClickNext: () => void;
}

interface RefundModalStep2Props {
  orderlines: (DetailedOrderLine & { cancelQuantity: number })[];
  paymentInfo: {
    pay_type: keyof typeof PAY_TYPE;
    card_issuer: string | null;
    installment_plan_months: number | null;
  };
  refundInfo: OrderCalculateRefundResponse;
  onClickPrev: () => void;
  onClickCancel: () => void;
  loading: boolean;
}

function QuantitySelector({
  max,
  cur,
  onInput,
}: {
  max: number;
  cur: number;
  onInput: (value: number) => void;
}) {
  const options = Array.from({ length: max }, (_, i) => i + 1);

  return (
    <S.QuantitySelectorContainer>
      <S.StyledSelect value={cur} onChange={(e) => onInput(Number(e.target.value))}>
        {options.map((option) => (
          <option key={option} value={option}>
            {option}
          </option>
        ))}
      </S.StyledSelect>
      <S.SelectArrow />
    </S.QuantitySelectorContainer>
  );
}

function RefundModalStep1({
  orderlines,
  checkedOrderlines,
  setCheckedOrderlines,
  handleQuantityChange,
  onCloseModal,
  onClickNext,
}: RefundModalStep1Props) {
  const [dialogOn, dialogOff] = useDialog();

  // 추가 상품만 남기려는 경우 취소 불가 모달 노출
  const checkCancelEnable = () => {
    const remainOrderline = orderlines.filter((orderline) => {
      const checkedOrderline = checkedOrderlines.find(
        (ol) => ol.orderline_id === orderline.orderline_id,
      );
      if (checkedOrderline) {
        return checkedOrderline.cancelQuantity < checkedOrderline.quantity;
      }
      return true;
    });
    const groupedOrderline = remainOrderline.reduce(
      (acc, cur) => {
        if (acc[cur.campaign_id]) {
          acc[cur.campaign_id].push(cur);
        } else {
          acc[cur.campaign_id] = [cur];
        }
        return acc;
      },
      {} as { [key: number]: DetailedOrderLine[] },
    );

    const onlyAdditionalProductLeft = Object.values(groupedOrderline).some((orderlines) =>
      orderlines.every((orderline) => orderline.additional_product_name),
    );

    if (onlyAdditionalProductLeft) {
      dialogOn({
        template: ORDER_TEMPLATE.refund.additionalProduct(),
        close: {
          text: '확인',
          onClick: dialogOff,
        },
      });
      return;
    }

    onClickNext();
  };

  return (
    <>
      <div style={{ marginBottom: '40px' }}>
        {orderlines.map((orderline) => (
          <S.Step1OrderlineContainer key={orderline.orderline_id}>
            <SelectBox
              value={checkedOrderlines.some((ol) => ol.orderline_id === orderline.orderline_id)}
              onInput={(e) => {
                const isChecked = e;
                setCheckedOrderlines((prev) =>
                  isChecked
                    ? [...prev, orderline]
                    : prev.filter((ol) => ol.orderline_id !== orderline.orderline_id),
                );
              }}
            />
            <div className="d-flex" style={{ gap: '10px', width: '100%' }}>
              <Thumbnail
                src={thumbnail.small(orderline.campaign_image[0].image)}
                alt={orderline.campaign_name}
              />
              <div className="d-flex flex-column justify-content-between" style={{ width: '100%' }}>
                <div className="d-flex flex-column" style={{ gap: '5px' }}>
                  <S.InlineParagraph2 className="text-bold">
                    {orderline.additional_product_name
                      ? `[추가 상품] ${orderline.campaign_name}`
                      : orderline.campaign_name}
                  </S.InlineParagraph2>
                  <SmallText color="gray500">
                    {orderline.additional_product_name
                      ? `${orderline.additional_product_name} / ${orderline.additional_option_name}`
                      : orderline.main_option_name
                      ? `${orderline.main_option_name} ${
                          orderline.sub_option_name ? ` / ${orderline.sub_option_name}` : ''
                        }`
                      : ``}
                  </SmallText>
                </div>
                <div className="quantity-container">
                  {orderline.quantity > 1 ? (
                    <>
                      <SmallText color="gray700">{orderline.quantity}개 중</SmallText>
                      <QuantitySelector
                        max={orderline.quantity}
                        cur={orderline.cancelQuantity}
                        onInput={(cancelQuantity) =>
                          handleQuantityChange(orderline.orderline_id, cancelQuantity)
                        }
                      />
                    </>
                  ) : (
                    <SmallText color="gray700">{orderline.quantity}개</SmallText>
                  )}
                </div>
              </div>
            </div>
          </S.Step1OrderlineContainer>
        ))}
      </div>
      <S.ModalFooter>
        <S.ButtonContainer>
          <Button style={{ flex: '3' }} size="medium" onClick={onCloseModal}>
            닫기
          </Button>
          <Button
            style={{ flex: '7' }}
            size="medium"
            type="primary"
            onClick={checkCancelEnable}
            disabled={checkedOrderlines.length === 0}
          >
            다음
          </Button>
        </S.ButtonContainer>
      </S.ModalFooter>
    </>
  );
}

function RefundModalStep2({
  orderlines,
  paymentInfo,
  refundInfo,
  onClickPrev,
  onClickCancel,
  loading,
}: RefundModalStep2Props) {
  const groupedOrderlines = orderlines.reduce(
    (acc, cur) => {
      if (acc[cur.campaign_id]) {
        acc[cur.campaign_id].push({
          ...cur,
          cancelQuantity: cur.cancelQuantity,
        });
      } else {
        acc[cur.campaign_id] = [
          {
            ...cur,
            cancelQuantity: cur.cancelQuantity,
          },
        ];
      }
      return acc;
    },
    {} as { [key: number]: (DetailedOrderLine & { cancelQuantity: number })[] },
  );
  const [infoClicked, setInfoClicked] = useState(false);
  const { is_full_cancel, cancel_data } = refundInfo;
  const onClickInfo = () => {
    setInfoClicked(!infoClicked);
  };

  const discountPrice = cancel_data.discount_price ?? 0;

  return (
    <>
      <div
        className="d-flex flex-column"
        style={{ gap: '20px', maxHeight: '230px', overflowY: 'scroll' }}
      >
        {Object.entries(groupedOrderlines).map(([campaignId, orderlines]) => (
          <Fragment key={campaignId}>
            <S.OrderGroupContainer>
              <Thumbnail
                src={thumbnail.small(orderlines[0].campaign_image[0].image)}
                alt={orderlines[0].campaign_name}
              />
              <div className="d-flex flex-column" style={{ gap: '5px' }}>
                <Paragraph2 className="text-bold">{orderlines[0].campaign_name}</Paragraph2>
                {orderlines.map((orderline) => (
                  <div key={orderline.orderline_id}>
                    <SmallText color="gray500">
                      {orderline.additional_product_name
                        ? `[추가 상품] ${orderline.additional_product_name} / ${orderline.additional_option_name} / ${orderline.cancelQuantity}개`
                        : orderline.main_option_name
                        ? `${orderline.main_option_name} / ${
                            orderline.sub_option_name ? `${orderline.sub_option_name} /` : ''
                          } ${orderline.cancelQuantity}개`
                        : `${orderline.cancelQuantity}개`}
                    </SmallText>
                  </div>
                ))}
                <SmallText className="text-bold">
                  {orderlines
                    .reduce((acc, cur) => acc + cur.unit_price * cur.cancelQuantity, 0)
                    .toLocaleString()}
                  원
                </SmallText>
              </div>
            </S.OrderGroupContainer>
          </Fragment>
        ))}
      </div>
      {/* 상품 정보 영역 */}
      <Divider spacing={20} />
      <S.AmountContainer>
        <S.RefundInfo>
          <Paragraph1>환불 예정 금액</Paragraph1>
          <Paragraph1 className="text-orange">
            {cancel_data.cancel_amount.toLocaleString()}원
          </Paragraph1>
        </S.RefundInfo>
        <DashDivider />
        <div className="d-flex flex-column" style={{ gap: '4px' }}>
          <S.RefundInfo>
            <Paragraph2 className="text-gray-800">총 결제 금액</Paragraph2>
            <Paragraph2 className="text-bold text-gray-700">
              {is_full_cancel
                ? (
                    cancel_data.total_product_price + cancel_data.current_shipping_fee
                  ).toLocaleString()
                : (cancel_data.total_product_price - discountPrice).toLocaleString()}
              원
            </Paragraph2>
          </S.RefundInfo>
          <S.RefundInfo>
            <SmallText className="text-gray-500">상품금액</SmallText>
            <SmallText className="text-gray-500">
              {cancel_data.total_product_price.toLocaleString()}원
            </SmallText>
          </S.RefundInfo>
          {cancel_data.current_shipping_fee > 0 && is_full_cancel && (
            <S.RefundInfo>
              <SmallText className="text-gray-500">배송비</SmallText>
              <SmallText className="text-gray-500">
                {cancel_data.current_shipping_fee.toLocaleString()}원
              </SmallText>
            </S.RefundInfo>
          )}
          {discountPrice > 0 && !is_full_cancel && (
            <S.RefundInfo>
              <SmallText className="text-gray-500">할인금액</SmallText>
              <SmallText className="text-gray-500">-{discountPrice.toLocaleString()}원</SmallText>
            </S.RefundInfo>
          )}
        </div>
        <S.RefundInfo style={{ marginTop: '15px' }}>
          <Paragraph2 className="text-gray-800">결제수단</Paragraph2>
          <Paragraph2 className="text-bold text-gray-700">{formatPayType(paymentInfo)}</Paragraph2>
        </S.RefundInfo>
        {cancel_data.additional_shipping_fee > 0 && (
          <>
            <DashDivider />
            <S.RefundInfo style={{ marginBottom: '6px' }}>
              <div className="d-flex align-items-center" style={{ gap: '4px' }}>
                <Paragraph2 className="text-gray-800">추가 발생 배송비</Paragraph2>
                <S.IconWrapper>
                  <S.InfoIcon onClick={onClickInfo} />
                  {infoClicked && (
                    <S.Tooltip>
                      <SmallText
                        className="text-white"
                        style={{ whiteSpace: 'nowrap', textAlign: 'start' }}
                      >
                        배송비의 경우 주문취소 금액에 따라
                        <br />
                        환불금액에서 차감될 수 있어요
                      </SmallText>
                    </S.Tooltip>
                  )}
                </S.IconWrapper>
              </div>
              <Paragraph2 className="text-gray-700">
                - {cancel_data.additional_shipping_fee.toLocaleString()} 원
              </Paragraph2>
            </S.RefundInfo>
          </>
        )}
        <Divider spacing={20} />
        <S.Callout>
          <ul>
            <li>
              <SmallText color="gray600">환불은 영업일 기준 2~4일 소요됩니다.</SmallText>
            </li>
            <li>
              <SmallText color="gray600">
                묶음 배송된 상품 중 일부를 취소/환불하는 경우, 남은 상품의 금액이 무료 배송 조건을
                충족하지 않으면 배송비가 부과됩니다.
              </SmallText>
            </li>
            <li>
              <SmallText color="gray600">
                배송비는 취소된 상품에 비례하여 줄어들거나 변하지 않습니다. (배송비는 묶음 단위로
                계산됩니다.)
              </SmallText>
            </li>
          </ul>
        </S.Callout>
      </S.AmountContainer>
      <S.ButtonContainer>
        <Button style={{ flex: '3' }} size="medium" onClick={onClickPrev} loading={loading}>
          이전
        </Button>
        <Button
          style={{ flex: '7' }}
          size="medium"
          type="primary"
          onClick={onClickCancel}
          loading={loading}
        >
          주문 취소하기
        </Button>
      </S.ButtonContainer>
    </>
  );
}

export function RefundModal(props: RefundModalProps & ExternalModalProps) {
  const [dialogOn, dialogOff] = useDialog();
  const queryClient = useQueryClient();
  const {
    externalShow,
    order_number,
    paymentInfo,
    orderlines,
    onClickCancel,
    onCloseModal,
    loading,
    csChannel,
  } = props;
  const [step, setStep] = useState<1 | 2>(1);
  const [orderLinesWithCancelQuantity, setOrderLinesWithCancelQuantity] = useState<
    (DetailedOrderLine & { cancelQuantity: number })[]
  >([]);
  const [checkedOrderlines, setCheckedOrderlines] = useState<
    (DetailedOrderLine & { cancelQuantity: number })[]
  >([]);
  const [refundInfo, setRefundInfo] = useState<OrderCalculateRefundResponse>({
    is_full_cancel: false,
    cancel_data: {
      cancel_amount: 0,
      total_product_price: 0,
      current_shipping_fee: 0,
      additional_shipping_fee: 0,
      discount_price: 0,
      exclude_from_additional_shipping: false,
    },
  });

  const cancel_data = checkedOrderlines.map((orderline) => {
    return {
      orderline_number: orderline.orderline_number,
      quantity: orderline.cancelQuantity,
    };
  });

  const handleQuantityChange = (orderlineId: number, cancelQuantity: number) => {
    setOrderLinesWithCancelQuantity((prev) =>
      prev.map((orderline) =>
        orderline.orderline_id === orderlineId ? { ...orderline, cancelQuantity } : orderline,
      ),
    );
    setCheckedOrderlines((prev) =>
      prev.map((orderline) =>
        orderline.orderline_id === orderlineId ? { ...orderline, cancelQuantity } : orderline,
      ),
    );
  };

  // 환불금액 계산 후
  const onClickCalculateRefund = async () => {
    try {
      const { status, data } = await api.orders.calculateRefund(order_number, cancel_data);
      if (status === 200 && data) {
        const {
          cancel_data: { exclude_from_additional_shipping },
        } = data;
        if (exclude_from_additional_shipping) {
          dialogOn({
            template: ORDER_TEMPLATE.refund.impossible(),
            confirm: {
              text: '문의하기',
              type: 'primary',
              onClick: () => {
                window.open(csChannel, '_blank');
                dialogOff();
              },
            },
          });
          return;
        }
        setRefundInfo(data);
        // 다음 단계로 이동
        setStep(2);
      }
    } catch (error) {
      handleCalculateRefundError(error);
    }
  };

  const handleCalculateRefundError = (error: unknown) => {
    const showRefundErrorDialog = () => {
      dialogOn({
        template: ORDER_TEMPLATE.refund.error(),
        confirm: {
          text: '문의하기',
          type: 'primary',
          onClick: () => {
            openPopup('https://inpocklink.channel.io/home', 375, 812);
            dialogOff();
          },
        },
      });
    };

    if (isAxiosError(error)) {
      const { response } = error;
      const { code, message } = response?.data;
      if (code === 'INVALID_VALUE') {
        dialogOn({
          template: REFUND_ERROR_TEMPLATE(message),
          close: {
            onClick: () => {
              dialogOff();
              if (message !== REFUND_ERROR_MESSAGE.notEnough) {
                queryClient.refetchQueries(keys.orders.all);
              }
            },
          },
        });
        return;
      }
      showRefundErrorDialog();
      return;
    }

    showRefundErrorDialog();
  };

  const handleOnClickCancel = () => {
    onClickCancel(cancel_data, refundInfo.cancel_data.cancel_amount);
  };

  useEffect(() => {
    setOrderLinesWithCancelQuantity(
      orderlines.map((orderline) => ({
        ...orderline,
        cancelQuantity: 1,
      })),
    );
  }, [orderlines]);

  return (
    <Modal
      type={MODAL_TYPES.CONFIRM}
      title="주문취소 🗑️"
      subTitle={REFUND_MODAL_STEP_MESSAGE[step]}
      onCloseModal={onCloseModal}
      externalShow={externalShow}
      disableOutsideClick={true}
      content={
        <div>
          <Divider spacing={[0, 20]} />
          {step === 1 && (
            <RefundModalStep1
              orderlines={orderLinesWithCancelQuantity}
              checkedOrderlines={checkedOrderlines}
              setCheckedOrderlines={setCheckedOrderlines}
              handleQuantityChange={handleQuantityChange}
              onCloseModal={onCloseModal}
              onClickNext={onClickCalculateRefund}
            />
          )}
          {step === 2 && (
            <RefundModalStep2
              orderlines={checkedOrderlines}
              paymentInfo={paymentInfo}
              refundInfo={refundInfo}
              onClickPrev={() => setStep(1)}
              onClickCancel={handleOnClickCancel}
              loading={loading}
            />
          )}
        </div>
      }
      footer={{}}
    />
  );
}
